import JSZip from 'jszip';
import type { Project } from '../domain/model';
import { safeMovieTitle } from '../domain/model';
import { getProjectFrames } from '../storage/projectRepository';
import { deliverMovie } from './deliverMovie';

async function blobBytes(blob: Blob): Promise<ArrayBuffer> {
  // Blobs restored from IndexedDB can lack arrayBuffer() in some environments.
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('The picture could not be read.'));
    reader.readAsArrayBuffer(blob);
  });
}

function frameExtension(type: string | undefined): string {
  if (type?.includes('png')) return 'png';
  if (type?.includes('webp')) return 'webp';
  return 'jpg';
}

export function zipFilename(project: Project): string {
  const base = safeMovieTitle(project.title, project.createdAt)
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .slice(0, 48) || 'my-movie';
  return `${base}-pictures.zip`;
}

export async function buildFramesZip(project: Project): Promise<Blob> {
  const { frames } = await getProjectFrames(project);
  if (frames.length === 0) {
    throw new Error('This movie has no pictures to save yet.');
  }
  const zip = new JSZip();
  const digits = String(frames.length).length;
  frames.forEach((frame, index) => {
    const name = `picture-${String(index + 1).padStart(Math.max(digits, 3), '0')}.${frameExtension(frame.blob.type)}`;
    zip.file(name, blobBytes(frame.blob), { date: new Date(frame.createdAt) });
  });
  return zip.generateAsync({ type: 'blob', mimeType: 'application/zip' });
}

export async function exportFramesZip(project: Project): Promise<'shared' | 'downloaded'> {
  const blob = await buildFramesZip(project);
  return deliverMovie(blob, zipFilename(project));
}
